import type React from "react"
import { Trash2, Building, Mail, User, MapPin } from "lucide-react"
import { Button } from "../ui/button"
import { Card, CardContent } from "../ui/card"

interface Firm {
  id: string
  name: string
  email: string
  law_firm: string
  state: string
}

interface FirmCardProps {
  firm: Firm
  onSelect: (firmId: string) => void
  onDelete: (firmId: string) => void
}

export default function FirmCard({ firm, onSelect, onDelete }: FirmCardProps) {
  return (
    <Card className="border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-200">
      <CardContent className="p-2">
        {/* Desktop Layout */}
        <div className="hidden md:flex justify-between items-center gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            {/* Name */}
            <div className="flex items-center gap-2 min-w-0 flex-shrink-0 w-48 lg:w-56">
              <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <h3
                className="text-sm font-semibold text-slate-900 cursor-pointer hover:text-slate-700 transition-colors truncate"
                onClick={() => onSelect(firm.id)}
              >
                {firm.name}
              </h3>
            </div>

            {/* Email */}
            <div className="flex items-center gap-2 flex-shrink-0 w-48 lg:w-56">
              <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <a href={`mailto:${firm.email}`} className="text-sm text-slate-700 hover:text-slate-900 transition-colors truncate">
                {firm.email}
              </a>
            </div>

            {/* Law Firm */}
            <div className="flex items-center gap-2 flex-shrink-0 w-48 lg:w-56">
              <Building className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span className="text-sm text-slate-700 truncate">
                {firm.law_firm}
              </span>
            </div>

            {/* State */}
            <div className="flex items-center gap-2 flex-shrink-0 w-24 lg:w-32">
              <MapPin className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <span className="text-sm text-slate-700 truncate">
                {firm.state}
              </span>
            </div>
          </div>

          {/* Delete Button */}
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100 flex-shrink-0"
            onClick={() => onDelete(firm.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Mobile Layout */}
        <div className="md:hidden">
          <div className="flex justify-between items-start mb-3">
            {/* Name and Contact Info */}
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <h3
                  className="text-sm font-semibold text-slate-900 cursor-pointer hover:text-slate-700 transition-colors truncate"
                  onClick={() => onSelect(firm.id)}
                >
                  {firm.name}
                </h3>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <Mail className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <a
                  href={`mailto:${firm.email}`}
                  className="text-sm text-slate-700 hover:text-slate-900 transition-colors"
                >
                  {firm.email}
                </a>
              </div>
              <div className="flex items-center gap-2 mb-1">
                <Building className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-700">
                  {firm.law_firm}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <span className="text-sm text-slate-700">
                  {firm.state}
                </span>
              </div>
            </div>

            {/* Delete Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
              onClick={() => onDelete(firm.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 